import { useMemo, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type { Field, Table } from '@/api/types'
import { useDocumentedDatabases, useTableFields } from '@/features/database/docs/queries'
import {
  BATCH_DELETE_EXAMPLE,
  batchRequestExample,
  batchResponseExample,
  batchDeleteUrl,
  fieldMapping,
  fieldsUrl,
  requestExample,
  responseExample,
  rowUrl,
  rowsUrl,
  tablesUrl,
  uploadFileUrl,
  uploadViaUrlUrl,
} from '@/features/database/docs/examples'
import { fieldResponseExample } from '@/features/database/fields/docs'
import {
  Endpoint,
  Parameter,
  Parameters,
  type ExampleSettings,
} from '@/features/database/docs/components/endpoint'
import {
  ErrorsTable,
  FieldsTable,
  FiltersTable,
} from '@/features/database/docs/components/reference'
import { backendUrl } from '@/shared/config/runtime'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { FILE_RESPONSE } from '@/features/database/fields/docs'
import { Skeleton } from '@/shared/ui/skeleton'

/**
 * The REST endpoints of one database, written against its own tables.
 *
 * Generated rather than written, which is the whole point: every example names
 * the reader's columns, carries values of the right shape for their types, and
 * is addressed at the backend this deployment was configured with -- so it can
 * be copied into a terminal and run.
 *
 * Two things upstream has are deliberately not here. Its filter-tree builder is
 * a second copy of the view filter editor M6 already built, and pointing that
 * one at a query parameter is a smaller piece of work than maintaining a
 * duplicate. And its sidebar highlights the section you have scrolled to by
 * measuring `offsetTop` on every scroll event; the contents list here links to
 * each section and lets the browser do the scrolling.
 */
export function ApiDocsDatabasePage() {
  const { t } = useTranslation()
  const { databaseId } = useParams({ from: '/api-docs/database/$databaseId' })
  const { databases, isPending } = useDocumentedDatabases()

  const found = databases.find((one) => one.database.id === databaseId)
  const database = found?.database
  const { tables, isPending: fieldsPending } = useTableFields(database)

  const [settings, setSettings] = useState<ExampleSettings>({
    language: 'curl',
    userFieldNames: true,
  })

  const backend = backendUrl()
  const documented = useMemo(
    () =>
      (database?.tables ?? []).flatMap((table) => {
        const held = tables.find((one) => one.tableId === table.id)
        return held === undefined ? [] : [{ table, fields: held.fields }]
      }),
    [database, tables],
  )

  if (isPending || fieldsPending) {
    return <Skeleton className="m-6 h-96" />
  }

  if (database === undefined) {
    return (
      <Empty className="min-h-dvh">
        <EmptyHeader>
          <EmptyTitle>{t('notFound.title')}</EmptyTitle>
        </EmptyHeader>
        <Button asChild variant="outline">
          <Link to="/api-docs">{t('apiDocs.title')}</Link>
        </Button>
      </Empty>
    )
  }

  const sections = [
    { id: 'introduction', label: t('apiDocs.introduction') },
    { id: 'authentication', label: t('apiDocs.authentication') },
    ...documented.map(({ table }) => ({
      id: `table-${table.id}`,
      label: table.name,
    })),
    { id: 'tables', label: t('apiDocs.listTables') },
    { id: 'files', label: t('apiDocs.uploadFile') },
    { id: 'errors', label: t('apiDocs.errors') },
  ]

  return (
    <div className="mx-auto w-full max-w-7xl gap-10 p-6 lg:flex">
      <nav aria-label={t('apiDocs.contents')} className="lg:w-56 lg:shrink-0">
        <div className="lg:sticky lg:top-6">
          <p className="text-muted-foreground mb-2 text-xs font-medium">
            {t('apiDocs.contents')}
          </p>
          <ul className="space-y-1">
            {sections.map((section) => (
              <li key={section.id}>
                {/* A real fragment link, so the browser does the scrolling and
                    the address bar says where the reader is -- which makes a
                    section something they can send to a colleague. */}
                <a
                  href={`#${section.id}`}
                  className="hover:text-foreground text-muted-foreground block truncate text-sm"
                >
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <main className="min-w-0 flex-1">
        <header className="flex flex-wrap items-start justify-between gap-4 pb-6">
          <div>
            <h1 className="text-2xl font-semibold">
              {t('apiDocs.pageTitle', { name: database.name })}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t('apiDocs.databaseId', { id: database.id })}
            </p>
          </div>
          {database.tables[0] !== undefined && (
            <Button asChild variant="outline" size="sm">
              <Link
                to="/database/$databaseId/table/$tableId"
                params={{
                  databaseId: database.id,
                  tableId: database.tables[0].id,
                }}
              >
                {t('apiDocs.openDatabase')}
              </Link>
            </Button>
          )}
        </header>

        <section id="introduction" className="scroll-mt-6 space-y-3 border-t py-8">
          <h2 className="text-lg font-semibold">{t('apiDocs.introduction')}</h2>
          <p className="text-muted-foreground text-sm">{t('apiDocs.intro')}</p>
          <p className="text-muted-foreground text-sm">{t('apiDocs.generated')}</p>
        </section>

        <section id="authentication" className="scroll-mt-6 space-y-3 border-t py-8">
          <h2 className="text-lg font-semibold">{t('apiDocs.authentication')}</h2>
          <p className="text-muted-foreground text-sm">
            {t('apiDocs.authenticationBody')}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings">{t('apiDocs.openSettings')}</Link>
          </Button>
        </section>

        {documented.map(({ table, fields }) => (
          <TableReference
            key={table.id}
            table={table}
            fields={fields}
            backend={backend}
            settings={settings}
            onSettings={setSettings}
          />
        ))}

        <section id="tables" className="scroll-mt-6 border-t pt-8">
          <h2 className="text-lg font-semibold">{t('apiDocs.listTables')}</h2>
        </section>
        <Endpoint
          id="list-tables"
          title={t('apiDocs.listTables')}
          method="GET"
          url={tablesUrl(backend, database.id)}
          description={<p>{t('apiDocs.listTablesBody')}</p>}
          response={[{ id: 0, name: 'string', order: 1, database_id: database.id }]}
          settings={settings}
          onSettings={setSettings}
          showUserFieldNames={false}
        />

        <section id="files" className="scroll-mt-6 border-t pt-8">
          <h2 className="text-lg font-semibold">{t('apiDocs.uploadFile')}</h2>
        </section>
        <Endpoint
          id="upload-file"
          title={t('apiDocs.uploadFile')}
          method="POST"
          url={uploadFileUrl(backend)}
          description={<p>{t('apiDocs.uploadFileBody')}</p>}
          filename="photo.png"
          parameters={
            <Parameters title={t('apiDocs.bodyParameters')}>
              <Parameter name="file" type="multipart" optional={false}>
                {t('apiDocs.paramFile')}
              </Parameter>
            </Parameters>
          }
          response={FILE_RESPONSE}
          settings={settings}
          onSettings={setSettings}
          showUserFieldNames={false}
        />
        <Endpoint
          id="upload-via-url"
          title={t('apiDocs.uploadViaUrl')}
          method="POST"
          url={uploadViaUrlUrl(backend)}
          description={<p>{t('apiDocs.uploadViaUrlBody')}</p>}
          parameters={
            <Parameters title={t('apiDocs.bodyParameters')}>
              <Parameter name="url" type="string" optional={false}>
                {t('apiDocs.paramUrl')}
              </Parameter>
            </Parameters>
          }
          request={{ url: 'https://example.com/photo.png' }}
          response={FILE_RESPONSE}
          settings={settings}
          onSettings={setSettings}
          showUserFieldNames={false}
        />

        <section id="errors" className="scroll-mt-6 space-y-3 border-t py-8">
          <h2 className="text-lg font-semibold">{t('apiDocs.errors')}</h2>
          <p className="text-muted-foreground text-sm">{t('apiDocs.errorsBody')}</p>
          <ErrorsTable />
        </section>
      </main>
    </div>
  )
}

/** Every endpoint of one table, and the columns they all speak in terms of. */
function TableReference({
  table,
  fields,
  backend,
  settings,
  onSettings,
}: {
  table: Table
  fields: Field[]
  backend: string
  settings: ExampleSettings
  onSettings: (settings: ExampleSettings) => void
}) {
  const { t } = useTranslation()
  const options = { userFieldNames: settings.userFieldNames }
  const mapping = fieldMapping(fields, options)
  const row = responseExample(fields, options)
  const listUrl = rowsUrl(backend, table.id, {
    userFieldNames: settings.userFieldNames,
  })
  const oneUrl = rowUrl(backend, table.id, {
    userFieldNames: settings.userFieldNames,
  })
  const batchUrl = rowsUrl(backend, table.id, {
    batch: true,
    userFieldNames: settings.userFieldNames,
  })
  const shared = { settings, onSettings, mapping }

  return (
    <>
      <section id={`table-${table.id}`} className="scroll-mt-6 space-y-4 border-t py-8">
        <h2 className="text-lg font-semibold">{table.name}</h2>
        <p className="text-muted-foreground text-sm">
          {t('apiDocs.tableId', { id: table.id })}
        </p>
        <p className="text-muted-foreground text-sm">
          {t('apiDocs.fieldsBody', { name: table.name })}
        </p>
        <FieldsTable fields={fields} userFieldNames={settings.userFieldNames} />
      </section>

      <Endpoint
        {...shared}
        id={`table-${table.id}-list-fields`}
        title={t('apiDocs.listFields')}
        method="GET"
        url={fieldsUrl(backend, table.id)}
        description={<p>{t('apiDocs.listFieldsBody', { name: table.name })}</p>}
        response={fields.map(fieldResponseExample)}
        showUserFieldNames={false}
      />

      <Endpoint
        {...shared}
        id={`table-${table.id}-list`}
        title={t('apiDocs.listRows')}
        method="GET"
        url={listUrl}
        description={<p>{t('apiDocs.listRowsBody', { name: table.name })}</p>}
        parameters={
          <Parameters title={t('apiDocs.queryParameters')}>
            <Parameter name="page" type="integer" standard="1">
              {t('apiDocs.paramPage')}
            </Parameter>
            <Parameter name="size" type="integer" standard="100">
              {t('apiDocs.paramSize')}
            </Parameter>
            <Parameter name="user_field_names" type="any">
              {t('apiDocs.userFieldNamesWhat')}
            </Parameter>
            <Parameter name="search" type="string">
              {t('apiDocs.paramSearch')}
            </Parameter>
            <Parameter name="order_by" type="string" standard="id">
              {t('apiDocs.paramOrderBy')}
            </Parameter>
            <Parameter name="filters" type="JSON">
              {t('apiDocs.paramFilters')}
            </Parameter>
            <Parameter name="filter__{field}__{condition}" type="string">
              {t('apiDocs.paramFilter')}
            </Parameter>
            <Parameter name="filter_type" type="string" standard="AND">
              {t('apiDocs.paramFilterType')}
            </Parameter>
            <Parameter name="include" type="string">
              {t('apiDocs.paramInclude')}
            </Parameter>
            <Parameter name="exclude" type="string">
              {t('apiDocs.paramExclude')}
            </Parameter>
            <Parameter name="view_id" type="integer">
              {t('apiDocs.paramViewId')}
            </Parameter>
            <Parameter name="{link_field}__join" type="string">
              {t('apiDocs.paramJoin')}
            </Parameter>
          </Parameters>
        }
        response={{
          count: 1024,
          next: `${rowsUrl(backend, table.id)}?page=2`,
          previous: null,
          results: [row],
        }}
      />

      <Endpoint
        {...shared}
        id={`table-${table.id}-get`}
        title={t('apiDocs.getRow')}
        method="GET"
        url={oneUrl}
        description={<p>{t('apiDocs.getRowBody', { name: table.name })}</p>}
        parameters={
          <Parameters title={t('apiDocs.pathParameters')}>
            <Parameter name="row_id" type="integer" optional={false}>
              {t('apiDocs.paramRowId')}
            </Parameter>
          </Parameters>
        }
        response={row}
      />

      <Endpoint
        {...shared}
        id={`table-${table.id}-create`}
        title={t('apiDocs.createRow')}
        method="POST"
        url={listUrl}
        description={<p>{t('apiDocs.createRowBody', { name: table.name })}</p>}
        parameters={
          <Parameters title={t('apiDocs.queryParameters')}>
            <Parameter name="before" type="integer">
              {t('apiDocs.paramBefore')}
            </Parameter>
            <Parameter name="send_webhook_events" type="any" standard="true">
              {t('apiDocs.paramSendWebhookEvents')}
            </Parameter>
          </Parameters>
        }
        request={requestExample(fields, options)}
        response={row}
      />

      <Endpoint
        {...shared}
        id={`table-${table.id}-create-batch`}
        title={t('apiDocs.createRows')}
        method="POST"
        url={batchUrl}
        description={<p>{t('apiDocs.createRowsBody', { name: table.name })}</p>}
        parameters={
          <Parameters title={t('apiDocs.bodyParameters')}>
            <Parameter name="items" type="array" optional={false}>
              {t('apiDocs.paramItems')}
            </Parameter>
          </Parameters>
        }
        request={{ items: [requestExample(fields, options)] }}
        response={batchResponseExample(fields, options)}
      />

      <Endpoint
        {...shared}
        id={`table-${table.id}-update`}
        title={t('apiDocs.updateRow')}
        method="PATCH"
        url={oneUrl}
        description={<p>{t('apiDocs.updateRowBody', { name: table.name })}</p>}
        parameters={
          <Parameters title={t('apiDocs.pathParameters')}>
            <Parameter name="row_id" type="integer" optional={false}>
              {t('apiDocs.paramRowId')}
            </Parameter>
          </Parameters>
        }
        request={requestExample(fields, options)}
        response={row}
      />

      <Endpoint
        {...shared}
        id={`table-${table.id}-update-batch`}
        title={t('apiDocs.updateRows')}
        method="PATCH"
        url={batchUrl}
        description={<p>{t('apiDocs.updateRowsBody', { name: table.name })}</p>}
        parameters={
          <Parameters title={t('apiDocs.bodyParameters')}>
            <Parameter name="items" type="array" optional={false}>
              {t('apiDocs.paramItems')}
            </Parameter>
          </Parameters>
        }
        request={batchRequestExample(fields, options)}
        response={batchResponseExample(fields, options)}
      />

      <Endpoint
        {...shared}
        id={`table-${table.id}-move`}
        title={t('apiDocs.moveRow')}
        method="PATCH"
        url={`${rowUrl(backend, table.id)}move/`}
        description={<p>{t('apiDocs.moveRowBody', { name: table.name })}</p>}
        parameters={
          <Parameters title={t('apiDocs.queryParameters')}>
            <Parameter name="before_id" type="integer">
              {t('apiDocs.paramBefore')}
            </Parameter>
          </Parameters>
        }
        response={row}
      />

      <Endpoint
        {...shared}
        id={`table-${table.id}-delete`}
        title={t('apiDocs.deleteRow')}
        method="DELETE"
        url={rowUrl(backend, table.id)}
        description={<p>{t('apiDocs.deleteRowBody', { name: table.name })}</p>}
        parameters={
          <Parameters title={t('apiDocs.pathParameters')}>
            <Parameter name="row_id" type="integer" optional={false}>
              {t('apiDocs.paramRowId')}
            </Parameter>
          </Parameters>
        }
        showUserFieldNames={false}
      />

      <Endpoint
        {...shared}
        id={`table-${table.id}-delete-batch`}
        title={t('apiDocs.deleteRows')}
        method="POST"
        url={batchDeleteUrl(backend, table.id)}
        description={<p>{t('apiDocs.deleteRowsBody', { name: table.name })}</p>}
        parameters={
          <Parameters title={t('apiDocs.bodyParameters')}>
            <Parameter name="items" type="array" optional={false}>
              {t('apiDocs.paramItemIds')}
            </Parameter>
          </Parameters>
        }
        request={BATCH_DELETE_EXAMPLE}
        showUserFieldNames={false}
      />

      <section
        id={`table-${table.id}-filters`}
        className="scroll-mt-6 space-y-4 border-t py-8"
      >
        <h3 className="text-lg font-semibold">{t('apiDocs.filters')}</h3>
        <p className="text-muted-foreground text-sm">{t('apiDocs.filtersBody')}</p>
        <FiltersTable fields={fields} userFieldNames={settings.userFieldNames} />
      </section>
    </>
  )
}
